import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInfiniteScans, useIntersectionObserver } from '../hooks/useInfiniteScans';
import { ScanListSkeleton, ScanCardSkeletonCompact } from '../components/loading/ScanCardSkeleton';
import { ScanCard } from '../components/scans/ScanCard';
import { CollapsibleSection } from '../components/disclosure';
import PixelSpinner from '../components/common/PixelSpinner';
import { featureFlags } from '../config/featureFlags';
import './ScansPage.css';

/**
 * ScansPage Component
 *
 * Main scans listing page with:
 * - Infinite scroll pagination
 * - Status filtering
 * - Loading states (skeleton loaders)
 * - Empty state handling
 * - Error state with retry
 * - Scroll-to-top functionality
 * - Responsive grid layout
 * - Full accessibility support
 */
const ScansPage = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Infinite scroll hook
  const {
    scans,
    totalCount,
    error,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    refetch,
  } = useInfiniteScans({
    status: statusFilter,
    limit: 20
  });

  // Intersection observer callback for infinite scroll
  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Sentinel element ref for infinite scroll trigger
  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    rootMargin: '200px', // Trigger 200px before reaching element
    threshold: 0.1,
  });

  // Handle scan card click — only navigate when the scan is FULLY complete.
  // This means scanning, enrichment and summary generation must all be done.
  const handleScanClick = useCallback((scanId, scanStatus) => {
    const isComplete = String(scanStatus || '').toLowerCase() === 'completed';
    if (!isComplete) return;
    navigate(`/scan/${scanId}`);
  }, [navigate]);

  // Handle status filter change
  const handleFilterChange = useCallback((status) => {
    setStatusFilter(status);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const activeScans = scans.filter((scan) =>
    ['queued', 'running', 'in_progress', 'analyzing', 'pending'].includes(String(scan.status || '').toLowerCase())
  );

  useEffect(() => {
    if (!featureFlags.realtimeScanUpdates || activeScans.length === 0) {
      return undefined;
    }
    const timer = setInterval(() => {
      refetch();
    }, 5000);
    return () => clearInterval(timer);
  }, [activeScans.length, refetch]);

  // Handle scroll-to-top button visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top handler
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Get filter button class
  const getFilterButtonClass = (filterValue) => {
    return `scans-page__filter-button ${
      statusFilter === filterValue ? 'scans-page__filter-button--active' : ''
    }`;
  };

  // Error state
  if (error) {
    return (
      <div className="scans-page">
        <div className="scans-page__header">
          <h1 className="scans-page__title">Security Scans</h1>
        </div>

        <div className="scans-page__error" role="alert">
          <div className="scans-page__error-icon" aria-hidden="true">️</div>
          <h2 className="scans-page__error-title">Failed to Load Scans</h2>
          <p className="scans-page__error-message">
            {error.message || 'An unexpected error occurred while loading your scans.'}
          </p>
          <button
            className="scans-page__error-button"
            onClick={() => refetch()}
            aria-label="Retry loading scans"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Initial loading state
  if (isLoading) {
    return (
      <div className="scans-page">
        <div className="scans-page__header">
          <h1 className="scans-page__title">Security Scans</h1>
          <div className="scans-page__subtitle">
            <span className="scans-page__loading-text" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <PixelSpinner size={24} /> Loading scans...
            </span>
          </div>
        </div>

        <div className="scans-page__filters">
          <button className="scans-page__filter-button scans-page__filter-button--active" disabled>
            All
          </button>
          <button className="scans-page__filter-button" disabled>Completed</button>
          <button className="scans-page__filter-button" disabled>Running</button>
          <button className="scans-page__filter-button" disabled>Failed</button>
        </div>

        <ScanListSkeleton count={6} />
      </div>
    );
  }

  // Empty state
  if (scans.length === 0) {
    return (
      <div className="scans-page">
        <div className="scans-page__header">
          <h1 className="scans-page__title">Security Scans</h1>
          <div className="scans-page__subtitle">
            <span className="scans-page__count">0 scans</span>
          </div>
        </div>

        <div className="scans-page__filters">
          <button
            className={getFilterButtonClass(null)}
            onClick={() => handleFilterChange(null)}
          >
            All
          </button>
          <button
            className={getFilterButtonClass('completed')}
            onClick={() => handleFilterChange('completed')}
          >
            Completed
          </button>
          <button
            className={getFilterButtonClass('running')}
            onClick={() => handleFilterChange('running')}
          >
            Running
          </button>
          <button
            className={getFilterButtonClass('failed')}
            onClick={() => handleFilterChange('failed')}
          >
            Failed
          </button>
        </div>

        <div className="scans-page__empty">
          <div className="scans-page__empty-icon" aria-hidden="true"></div>
          <h2 className="scans-page__empty-title">
            {statusFilter ? `No ${statusFilter} scans found` : 'No scans found'}
          </h2>
          <p className="scans-page__empty-message">
            {statusFilter
              ? `You don't have any ${statusFilter} scans yet. Try changing the filter or start a new scan.`
              : 'Get started by running your first security scan to identify vulnerabilities in your applications.'
            }
          </p>
          <div className="scans-page__empty-actions">
            {statusFilter && (
              <button
                className="scans-page__empty-button scans-page__empty-button--secondary"
                onClick={() => handleFilterChange(null)}
              >
                Clear Filter
              </button>
            )}
            <button
              className="scans-page__empty-button scans-page__empty-button--primary"
              onClick={() => navigate('/')}
            >
              Start New Scan
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main content with scans
  return (
    <div className="scans-page">
      {/* Header */}
      <div className="scans-page__header">
        <h1 className="scans-page__title">Security Scans</h1>
        <div className="scans-page__subtitle">
          <span className="scans-page__count">
            {totalCount.toLocaleString()} {totalCount === 1 ? 'scan' : 'scans'}
          </span>
          {statusFilter && (
            <span className="scans-page__filter-indicator">
              • Filtered by: <strong>{statusFilter}</strong>
            </span>
          )}
        </div>
      </div>

      {/* Filters */}
      <CollapsibleSection
        id="scans-page-filters"
        header={<span>Filters {statusFilter ? `(${statusFilter})` : ''}</span>}
        defaultExpanded
        className="scans-page__filter-section"
      >
        <div className="scans-page__filters" role="group" aria-label="Scan status filters">
          <button
            className={getFilterButtonClass(null)}
            onClick={() => handleFilterChange(null)}
            aria-pressed={statusFilter === null}
          >
            All
          </button>
          <button
            className={getFilterButtonClass('completed')}
            onClick={() => handleFilterChange('completed')}
            aria-pressed={statusFilter === 'completed'}
          >
            Completed
          </button>
          <button
            className={getFilterButtonClass('running')}
            onClick={() => handleFilterChange('running')}
            aria-pressed={statusFilter === 'running'}
          >
            Running
          </button>
          <button
            className={getFilterButtonClass('failed')}
            onClick={() => handleFilterChange('failed')}
            aria-pressed={statusFilter === 'failed'}
          >
            Failed
          </button>
        </div>
      </CollapsibleSection>

      {activeScans.length > 0 && (
        <div className="scans-page__live-summary" role="status">
          <span>{activeScans.length} active scan{activeScans.length === 1 ? '' : 's'}</span>
          <span>Auto-refresh every 5s</span>
        </div>
      )}

      {/* Scans Grid */}
      <div className="scans-page__grid" role="list">
        {scans.map((scan) => (
          <div key={scan.scan_id} role="listitem">
            <ScanCard
              scan={scan}
              onClick={handleScanClick}
              expandable={false}
            />
          </div>
        ))}
      </div>

      {/* Infinite Scroll Sentinel */}
      {hasNextPage && (
        <div
          ref={sentinelRef}
          className="scans-page__sentinel"
          aria-live="polite"
          aria-busy={isFetchingNextPage}
        >
          {isFetchingNextPage && (
            <div className="scans-page__loading-more">
              <ScanCardSkeletonCompact />
            </div>
          )}
        </div>
      )}

      {/* End of List Indicator */}
      {!hasNextPage && scans.length > 0 && (
        <div className="scans-page__end-message" role="status">
          <p>You've reached the end of the list</p>
          <button
            className="scans-page__scroll-top-inline"
            onClick={scrollToTop}
            aria-label="Scroll to top"
          >
            ↑ Back to Top
          </button>
        </div>
      )}

      {/* Floating Scroll-to-Top Button */}
      {showScrollTop && (
        <button
          className="scans-page__scroll-top-button"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          title="Scroll to top"
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default ScansPage;

// Made with Bob
